import { ClipboardList, Gauge, Wrench } from 'lucide-react'
import { useVehicles, useServiceRecords } from '../api/hooks'

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest text-xs
                     bg-yellow-400 text-black px-2 py-0.5 rounded">
      {registration}
    </span>
  )
}

function RecordRow({ record }) {
  const name = record.item_name || record.custom_name || 'Service'
  const date = new Date(record.service_date).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  })

  return (
    <div className="flex items-start gap-4 px-5 py-4">
      <div className="w-8 h-8 rounded-lg bg-indigo-600/10 flex items-center justify-center shrink-0 mt-0.5">
        <Wrench size={14} className="text-indigo-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-[var(--text-primary)]">{name}</p>
              {record.is_finalised && (
                <span className="px-1.5 py-0.5 text-xs bg-indigo-500/15 text-indigo-400 rounded-full">
                  Finalised
                </span>
              )}
            </div>
            {record.performed_by && (
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">{record.performed_by}</p>
            )}
            {record.notes && (
              <p className="text-xs text-[var(--text-muted)] mt-1 line-clamp-2">{record.notes}</p>
            )}
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-[var(--text-secondary)]">{date}</p>
            {record.mileage && (
              <p className="text-xs text-[var(--text-muted)] mt-0.5 flex items-center gap-1 justify-end">
                <Gauge size={11} />
                {Number(record.mileage).toLocaleString()}
              </p>
            )}
            {record.cost && (
              <p className="text-xs font-medium text-[var(--text-secondary)] mt-0.5">
                £{parseFloat(record.cost).toFixed(2)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function VehicleHistorySection({ vehicle }) {
  const { data: records = [], isLoading } = useServiceRecords(vehicle.id)

  if (!isLoading && records.length === 0) return null

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--border)]">
        <Plate registration={vehicle.registration} />
        <span className="font-medium text-[var(--text-primary)] text-sm">
          {vehicle.make} {vehicle.model}
        </span>
        {!isLoading && records.length > 0 && (
          <span className="ml-auto text-xs text-[var(--text-muted)]">
            {records.length} {records.length === 1 ? 'record' : 'records'}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="p-4 space-y-3 animate-pulse">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-12 bg-[var(--bg-overlay)] rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="divide-y divide-[var(--border)]">
          {records.map(r => <RecordRow key={r.id} record={r} />)}
        </div>
      )}
    </div>
  )
}

export default function ServiceHistoryPage() {
  const { data: vehicles = [], isLoading } = useVehicles()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Service History</h1>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          All recorded services across your vehicles.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-40 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : vehicles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] flex items-center justify-center mb-4">
            <ClipboardList size={28} className="text-[var(--text-muted)]" />
          </div>
          <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">No history yet</h3>
          <p className="text-sm text-[var(--text-secondary)] max-w-xs">
            Service records will appear here after you log services from the vehicle schedule.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {vehicles.map(v => <VehicleHistorySection key={v.id} vehicle={v} />)}
        </div>
      )}
    </div>
  )
}
